import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Send, Eye, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEGMENTS = [
  { value: 'customers', label: 'All Customers' },
  { value: 'investors', label: 'All Investors' },
  { value: 'admins', label: 'All Admins' },
  { value: 'all', label: 'All Users (customers + investors)' },
];

const ManualSendTab = ({ templates }) => {
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [templateKey, setTemplateKey] = useState(templates[0]?.key || '');
  const [recipient, setRecipient] = useState('');
  const [segment, setSegment] = useState('customers');
  const [contextJson, setContextJson] = useState('{"full_name":"there"}');
  const [sending, setSending] = useState(false);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);

  const selectedTpl = templates.find((t) => t.key === templateKey);

  const refreshPreview = useCallback(async () => {
    if (!templateKey) return;
    try {
      const ctx = contextJson ? JSON.parse(contextJson) : {};
      const res = await axios.post(`${API}/admin/email-templates/${templateKey}/preview`, { context: ctx });
      setPreviewSubject(res.data.subject || '');
      setPreviewHtml(res.data.html || '');
    } catch (err) {
      // Most likely invalid JSON, silently keep last preview
    }
  }, [templateKey, contextJson]);

  useEffect(() => { refreshPreview(); }, [refreshPreview]);

  const parseContext = () => {
    try {
      return contextJson.trim() ? JSON.parse(contextJson) : {};
    } catch {
      toast.error('Context is not valid JSON');
      return null;
    }
  };

  const handleSingleSend = async () => {
    const ctx = parseContext();
    if (ctx === null) return;
    if (!recipient.trim()) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/email-templates/send`, {
        template_key: templateKey,
        recipient: recipient.trim(),
        context: ctx,
      });
      if (res.data.success) {
        toast.success(`Email sent to ${recipient}`);
      } else {
        toast.warning('Send returned a failure — check logs');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleBulkPreview = async () => {
    const ctx = parseContext();
    if (ctx === null) return;
    try {
      const res = await axios.post(`${API}/admin/email-templates/send-bulk`, {
        template_key: templateKey,
        segment,
        context: ctx,
        test_only: true,
      });
      setBulkPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    }
  };

  const handleBulkSend = async () => {
    const ctx = parseContext();
    if (ctx === null) return;
    if (!bulkPreview) { toast.warning('Click "Preview recipients" first'); return; }
    if (!window.confirm(`Send '${selectedTpl?.name}' to ${bulkPreview.would_send_to} recipients?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/email-templates/send-bulk`, {
        template_key: templateKey,
        segment,
        context: ctx,
      });
      toast.success(`Sent ${res.data.sent} · failed/skipped ${res.data.failed_or_skipped}`);
      setBulkPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="manual-send-tab">
      <div className="flex gap-2">
        {['single', 'bulk'].map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setBulkPreview(null); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${
              mode === m ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`send-mode-${m}`}
          >
            {m === 'single' ? 'Single Recipient' : 'Bulk / Segment'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Template</label>
              <select
                value={templateKey}
                onChange={(e) => setTemplateKey(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="send-template-select"
              >
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            </div>

            {mode === 'single' ? (
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Recipient email</label>
                <Input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="user@example.com"
                  data-testid="send-recipient-input"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Segment</label>
                <select
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  data-testid="send-segment-select"
                >
                  {SEGMENTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <p className="text-xs text-amber-700 mt-2 flex items-start gap-1">
                  <Users className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  Bulk sends are capped at 500 recipients per call. Split larger segments.
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-600">Context (JSON)</label>
                {selectedTpl?.variables?.length > 0 && (
                  <span className="text-[10px] text-gray-400">
                    keys: {selectedTpl.variables.join(', ')}
                  </span>
                )}
              </div>
              <textarea
                className="w-full font-mono text-xs border rounded-md p-2 min-h-[120px]"
                value={contextJson}
                onChange={(e) => setContextJson(e.target.value)}
                data-testid="send-context-textarea"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                In bulk mode, <code>full_name</code> and <code>email</code> are auto-filled per
                recipient — your overrides here apply to everyone.
              </p>
            </div>

            {mode === 'single' ? (
              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={handleSingleSend}
                disabled={sending}
                data-testid="send-single-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {sending ? 'Sending…' : 'Send Email'}
              </Button>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={handleBulkPreview}
                  data-testid="send-bulk-preview-btn"
                >
                  <Eye className="h-4 w-4 mr-2" /> Preview recipients
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleBulkSend}
                  disabled={sending || !bulkPreview || (bulkPreview?.would_send_to ?? 0) > 500}
                  data-testid="send-bulk-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {sending ? 'Sending…' : `Send to ${bulkPreview?.would_send_to ?? '?'}`}
                </Button>
              </div>
            )}

            {bulkPreview && bulkPreview.would_send_to > 500 && (
              <p className="text-xs text-red-600 -mt-1">
                ⚠ {bulkPreview.would_send_to} recipients exceeds the 500-per-call limit. Split this segment.
              </p>
            )}

            {bulkPreview && (
              <div className="text-xs bg-gray-50 border rounded-md p-3">
                <p className="font-semibold">{bulkPreview.would_send_to} recipients in '{segment}'.</p>
                <p className="text-gray-500 mt-1">Sample:</p>
                <ul className="mt-1 space-y-0.5">
                  {bulkPreview.preview_recipients?.map((r) => (
                    <li key={r.email} className="text-gray-600">
                      • {r.email} {r.full_name && <span className="text-gray-400">— {r.full_name}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-2">
              <Eye className="h-3.5 w-3.5" /> Preview
            </label>
            <div className="border rounded-md p-2 bg-gray-50">
              <p className="text-xs font-medium text-gray-700 mb-2">{previewSubject}</p>
              <iframe
                title="send-preview"
                srcDoc={previewHtml}
                className="w-full bg-white rounded"
                style={{ minHeight: 460, border: 'none' }}
                data-testid="send-preview-iframe"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ManualSendTab;
